/**
 * TUAC AudioWorklet 引擎封装
 * - 负责加载 worklet、IR、参数下发
 * - 对外提供 input/output 以接入现有音频链
 */
import { sendClientDebugToServer } from './constants';
import { createTuacScriptProcessor } from './tuacScriptProcessorFallback';

const IR_LIBRARY = {
  hall: '1_Halls_01_Large_Hall_48K.wav',
  plate: '2_Plates_03_London_Plate_48K.wav',
  studio: '3_Rooms_01_Studio_A_48K.wav',
  ambience: '5_Ambiences_02_Medium_Ambience_48K.wav'
};

const clamp01 = (value) => {
  const v = Number(value);
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(1, v));
};

const clampRange = (value, min, max) => {
  const v = Number(value);
  if (!Number.isFinite(v)) return min;
  return Math.max(min, Math.min(max, v));
};

const getBuildVersion = () => {
  const versionKey = 'tenryu:build-version';
  if (typeof window === 'undefined' || !window.localStorage) return '';
  return window.localStorage.getItem(versionKey) || '';
};

export default class TuacAudioEngine {
  static _workletLoaders = new WeakMap();
  static DEBUG = false;
  // 运行时自适应：默认全质量；若检测到“咔咔/断续”则逐级降载
  static _irMaxChannels = 4;

  static ensureWorklet(ctx) {
    if (!ctx) return Promise.reject(new Error('AudioContext not ready'));
    // audioWorklet 仅在安全上下文（HTTPS 或 localhost）下可用，非安全上下文下为 undefined
    if (!ctx.audioWorklet) {
      const diag = typeof window !== 'undefined' ? {
        isSecureContext: window.isSecureContext,
        origin: window.location?.origin,
        contextState: ctx.state,
        contextCtor: ctx.constructor?.name
      } : { contextState: ctx.state };
      sendClientDebugToServer('TUAC', 'audioWorklet undefined (likely non-HTTPS)', diag);
      return Promise.reject(new Error('AudioWorklet not available (requires secure context)'));
    }
    const existing = TuacAudioEngine._workletLoaders.get(ctx);
    if (existing) return existing;
    const version = getBuildVersion();
    const moduleUrl = version
      ? `/tuac/tuac_processor.js?v=${encodeURIComponent(version)}`
      : '/tuac/tuac_processor.js';
    const loader = ctx.audioWorklet.addModule(moduleUrl);
    TuacAudioEngine._workletLoaders.set(ctx, loader);
    // 失败时清除缓存，避免同一 context 永久失败（如三星首次加载失败后不再重试）
    loader.catch((err) => {
      TuacAudioEngine._workletLoaders.delete(ctx);
      sendClientDebugToServer('TUAC', 'addModule failed', err);
    });
    return loader;
  }

  constructor(audioCtx) {
    this.ctx = audioCtx;
    this.node = null;
    this._loadId = 0;
    this._ready = false;
    this._readyPromise = null;
    this._pendingParams = null;
    this._currentIrMode = null;
    this._lastParams = null;
    this._adaptiveLevel = 0; // 0=全质量，1=轻降载，2=强降载（IR 通道降为 2）
    this._lastGlitchMs = 0;
    this._pendingIrReload = false;
    this._scriptProcessorFallback = false; // 降级为 ScriptProcessorNode 时为 true
  }

  /**
   * 当不支持 AudioWorkletNode 时，降级使用 ScriptProcessorNode + 主线程同一份 tuac_core WASM。
   */
  async _createScriptProcessorFallback() {
    if (this.node) return;
    try {
      const result = await createTuacScriptProcessor(this.ctx, {
        onGlitch: (data) => {
          try {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('app:tuac-glitch', { detail: data }));
            }
          } catch (_) {}
          this._handleGlitch(data);
        }
      });
      this.node = result.node;
      this._scriptProcessorFallbackApi = {
        setParams: result.setParams,
        loadIRChannel: result.loadIRChannel
      };
      this._scriptProcessorFallbackDestroy = result.destroy;
      this._scriptProcessorFallback = true;
      this._ready = true;
      sendClientDebugToServer('TUAC', 'using ScriptProcessorNode + WASM fallback');
    } catch (err) {
      sendClientDebugToServer('TUAC', 'ScriptProcessorNode + WASM fallback failed', err);
    }
  }

  /** 释放引擎资源（context 切换或卸载时调用，主线程 WASM 降级会断开节点并释放缓冲） */
  dispose() {
    if (this._scriptProcessorFallbackDestroy) {
      try {
        this._scriptProcessorFallbackDestroy();
      } catch (_) {}
      this._scriptProcessorFallbackDestroy = null;
    }
    this._scriptProcessorFallbackApi = null;
    this.node = null;
    this._ready = false;
    this._readyPromise = null;
  }

  get input() {
    return this.node;
  }

  get output() {
    return this.node;
  }

  async _ensureReady() {
    if (this._ready) return true;
    if (this._readyPromise) return this._readyPromise;

    const hasAudioWorkletNode = typeof window !== 'undefined' && typeof window.AudioWorkletNode === 'function';
    if (!hasAudioWorkletNode) {
      this._readyPromise = (async () => {
        await this._createScriptProcessorFallback();
        return true;
      })();
      return this._readyPromise;
    }

    this._readyPromise = (async () => {
      try {
        await TuacAudioEngine.ensureWorklet(this.ctx);
        if (!this.node) {
          this.node = new AudioWorkletNode(this.ctx, 'tuac-processor', {
            outputChannelCount: [2]
          });
          this.node.port.onmessage = (event) => {
            if (event?.data?.type === 'ENGINE_LOADED') {
              this._ready = true;
              if (this._pendingParams) {
                this.setParams(this._pendingParams);
                this._pendingParams = null;
              }
            } else if (event?.data?.type === 'GLITCH') {
              try {
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('app:tuac-glitch', { detail: event.data }));
                }
              } catch (_) {}
              this._handleGlitch(event.data);
            } else if (TuacAudioEngine.DEBUG && event?.data?.type === 'IR_LOADED') {
                console.info('[TUAC] IR loaded:', event.data);
            } else if (TuacAudioEngine.DEBUG && event?.data?.type === 'PARAM_APPLIED') {
                console.info('[TUAC] params applied:', event.data);
            }
          };
        }
        return true;
      } catch (_) {
        await this._createScriptProcessorFallback();
        return true;
      }
    })();
    this._readyPromise.catch(() => {
      this._readyPromise = null;
    });
    return this._readyPromise;
  }

  _handleGlitch(data) {
    try {
      const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      // 避免过于频繁地触发降载
      if (now - (this._lastGlitchMs || 0) < 1500) return;
      this._lastGlitchMs = now;

      const prev = this._adaptiveLevel || 0;
      if (prev >= 2) return;
      this._adaptiveLevel = prev + 1;

      if (TuacAudioEngine.DEBUG) {
        console.warn('[TUAC] glitch detected -> adaptive level', this._adaptiveLevel, data);
      }

      // Level 1：仅参数降载（关 deconstruction / 轻度降低空间强度）
      // Level 2：IR 通道降为 2 + 触发一次 IR 重载（更明显减负）
      if (this._adaptiveLevel >= 2) {
        TuacAudioEngine._irMaxChannels = Math.min(2, TuacAudioEngine._irMaxChannels || 4);
        this._scheduleIrReload();
      }

      // 重新下发参数（会应用自适应 caps）
      if (this._lastParams) this.setParams(this._lastParams);
    } catch (_) {}
  }

  _scheduleIrReload() {
    if (this._pendingIrReload) return;
    if (!this._currentIrMode) return;
    this._pendingIrReload = true;
    const mode = this._currentIrMode;
    // 异步重载 IR：避免在 onmessage 栈里直接 await
    setTimeout(() => {
      this._pendingIrReload = false;
      // 仅在仍处于强降载时重载
      if ((this._adaptiveLevel || 0) < 2) return;
      this._currentIrMode = null;
      this.loadEnvironment(mode).catch(() => {});
    }, 0);
  }

  async loadEnvironment(mode) {
    await this._ensureReady();
    const key = IR_LIBRARY[mode] ? mode : 'studio';
    if (this._currentIrMode === key) return;
    const filename = IR_LIBRARY[key];
    const version = getBuildVersion();
    const url = version
      ? `/tuac/audio/${encodeURIComponent(filename)}?v=${encodeURIComponent(version)}`
      : `/tuac/audio/${encodeURIComponent(filename)}`;

    const loadId = ++this._loadId;
    const res = await fetch(url);
    if (!res.ok) throw new Error('IR load failed: ' + res.status);
    const data = await res.arrayBuffer();
    const buffer = await this.ctx.decodeAudioData(data);
    if (loadId !== this._loadId) return;

    const resampleIfNeeded = (pcm) => {
      try {
        const src = pcm;
        const srcSr = buffer.sampleRate || 0;
        const dstSr = this.ctx.sampleRate || 0;
        if (!src || !src.length) return src;
        if (!srcSr || !dstSr || srcSr === dstSr) return src;
        const ratio = dstSr / srcSr;
        const dstLen = Math.max(1, Math.round(src.length * ratio));
        const out = new Float32Array(dstLen);
        const scale = (src.length - 1) / Math.max(1, (dstLen - 1));
        for (let i = 0; i < dstLen; i += 1) {
          const x = i * scale;
          const ix = Math.floor(x);
          const t = x - ix;
          const a = src[ix] || 0;
          const b = src[Math.min(src.length - 1, ix + 1)] || 0;
          out[i] = a + (b - a) * t;
        }
        return out;
      } catch (_) {
        return pcm;
      }
    };

    const channels = Math.min(TuacAudioEngine._irMaxChannels || 4, buffer.numberOfChannels || 0);
    if (this._scriptProcessorFallback && this._scriptProcessorFallbackApi) {
      for (let i = 0; i < channels; i += 1) {
        this._scriptProcessorFallbackApi.loadIRChannel(i, resampleIfNeeded(buffer.getChannelData(i)));
      }
    } else {
      for (let i = 0; i < channels; i += 1) {
        this.node.port.postMessage({
          type: 'LOAD_IR_CHANNEL',
          index: i,
          pcmData: resampleIfNeeded(buffer.getChannelData(i))
        });
      }
    }
    this._currentIrMode = key;
    if (this._lastParams) {
      this.setParams(this._lastParams);
    }
  }

  /** 接收来自 tuac_core.inf [tuac] 及播放器 overrides 的参数，提交给 TUAC worklet 或主线程 WASM 降级 */
  setParams(params = {}) {
    const {
      fidelity,
      warmth,
      thickness,
      spatial,
      reverbLevel,
      bassIntensity,
      inputGain,
      spatialDeconstruction,
      vocalFocus,
      elevation,
      distance,
      roomDepth,
      humidity,
      airHighPass,
      smoothing
    } = params;

    this._lastParams = { ...params };
    const level = this._adaptiveLevel || 0;
    const spatialRaw = clamp01(spatial ?? 0);
    // 自适应：通过限制 reverb 参与的 wetScale，间接降低 effectiveSpatial（减轻卷积负载的“听感强度”）
    const reverbCap = level >= 2 ? 0.05 : (level >= 1 ? 0.07 : 1.0);
    const reverb = clamp01(Math.min((reverbLevel ?? 0.5), reverbCap));
    const wetScale = 0.2 + (reverb * 0.8);
    const spatialScale = level >= 2 ? 0.75 : (level >= 1 ? 0.88 : 1.0);
    const effectiveSpatial = clampRange(spatialRaw * wetScale * 2.1 * spatialScale, 0, 1.0);

    const payload = { type: 'UPDATE_PARAMS' };
    if (fidelity !== undefined) {
      const fidelityCap = level >= 2 ? 0.35 : (level >= 1 ? 0.45 : 1.0);
      payload.fidelity = clamp01(Math.min(fidelity, fidelityCap));
    }
    if (warmth !== undefined) payload.warmth = clamp01(warmth);
    if (thickness !== undefined) payload.thickness = clamp01(thickness);
    payload.spatial = effectiveSpatial;
    if (bassIntensity !== undefined) payload.bassIntensity = clamp01(bassIntensity);
    if (inputGain !== undefined) payload.inputGain = clamp01(inputGain);
    if (spatialDeconstruction !== undefined) {
      // 自适应：一旦出现 glitch，优先关闭最吃 CPU 的 deconstruction
      payload.spatialDeconstruction = level >= 1 ? false : !!spatialDeconstruction;
    }
    if (vocalFocus !== undefined) payload.vocalFocus = clamp01(vocalFocus);
    if (elevation !== undefined) payload.elevation = Number(elevation);
    if (distance !== undefined) payload.distance = Number(distance);
    if (roomDepth !== undefined) payload.roomDepth = Number(roomDepth);
    if (humidity !== undefined) payload.humidity = clamp01(humidity);
    if (airHighPass !== undefined) payload.airHighPass = clamp01(airHighPass);
    if (smoothing !== undefined) payload.smoothing = clamp01(smoothing);

    if (this._scriptProcessorFallback && this._scriptProcessorFallbackApi) {
      this._scriptProcessorFallbackApi.setParams(payload);
      return;
    }
    if (!this._ready || !this.node) {
      this._pendingParams = {
        ...this._pendingParams,
        ...params,
        spatial: effectiveSpatial
      };
      return;
    }
    this.node.port.postMessage(payload);
  }
}
